import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { useMocks } from '../../../shared/env';
import { ChartDataTable } from '../../../shared/dashboard/ChartDataTable';
import { StatusBadge } from '../../../shared/dashboard/StatusBadge';
import { UnavailableState } from '../../../shared/dashboard/UnavailableState';
import { MetricInfo } from '../../../shared/dashboard/MetricInfo';
import { IdentityContext } from '../../identity/state/IdentityContext';
import { AnnouncementsPanel } from '../../dashboard-v2/components/AnnouncementsPanel';
import { DonutChart } from '../../dashboard-v2/components/DonutChart';
import { CountStatCardBody, MoneyStatCardBody } from '../../dashboard-v2/components/StatCard';
import { dashboardGateway } from '../../dashboard-v2/index';
import { SYNTHETIC_DEMO_LABEL } from '../../dashboard-v2/syntheticExecutive';
import { useMetricDictionary, type MetricDictionaryView } from '../../dashboard-v2/useMetricDictionary';
import {
  PAYROLL_BREAKDOWN_COLORS, formatCurrency,
  type DashboardSummaryResponse, type ExecutiveCostTotals, type ExecutiveDashboardResponse, type ExecutiveQuery, type Variance,
} from '../../dashboard-v2/types';
import { AdminProductOfferingsRow } from '../components/AdminProductOfferingsRow';
import { ExecutiveAlertsPanel, ObligationsPanel, PayrollQueuePanel } from '../components/ExecutivePanels';
import { ExecutiveDashboardHeader } from '../components/ExecutiveDashboardHeader';
import { LaborCostTrend } from '../components/LaborCostTrend';
import { PeriodControls } from '../components/PeriodControls';
import { parseExecutiveQuery, toParams } from '../periodQuery';
import { TrialStatusCard } from '../../subscription/TrialStatusCard';
import { useCurrentSubscription } from '../../subscription/useCurrentSubscription';
import '../../dashboard-v2/dashboard.css';
import '../../../shared/dashboard/dashboardPrimitives.css';
import './AdminDashboardPage.css';

/**
 * The company's Executive Payroll and Workforce Cost dashboard, hosted inside the normal AppShell at /dashboards/admin-dashboard (the standalone,
 * shell-less /dashboard is a separate page that shares the monthly summary API).
 *
 * Reading order, top to bottom: who and what period (header) and the period choice -> the headline figures, where the money went, and twelve months of cost
 * -> payroll status -> alerts and upcoming obligations -> announcements -> what the page cannot show yet -> product offerings. Executive information comes
 * before any marketing content.
 *
 * Two server calls feed it, loaded independently so one failing never blanks the other: `GET /api/executive-dashboard` (cost totals, comparison, monthly
 * trend, payroll status, filing dates, alerts, for the chosen period) and `GET /api/dashboard-summary` (taxes due this month, unpaid tax amounts,
 * announcements). Both add up the same payroll totals through one shared provider on the server, and nothing here recomputes a wage, tax or deduction. When
 * a source is unreachable the affected card says "Data unavailable" (a null from the server, never a zero) and every other section stays usable. The
 * explanation behind each figure comes from the server's metric dictionary (`GET /api/dashboard-metrics`).
 *
 * The chosen period lives in the page address (`?period=...`, and `from`/`to` for a custom range), so a link, a reload and the browser's back and forward
 * buttons all give the same view.
 *
 * Layout notes kept from the earlier approved composition: AppShell's shared `.content` wrapper caps at max-width: 900px, which AdminDashboardPage.css
 * lifts for this page only; the "Payroll Tax Breakdown" card stays as the approved placeholder because the tax engine does not yet supply
 * jurisdiction-level categories.
 */
export function AdminDashboardPage() {
  const [summary, setSummary] = useState<DashboardSummaryResponse | null>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [exec, setExec] = useState<ExecutiveDashboardResponse | null>(null);
  const [execError, setExecError] = useState<string | null>(null);
  const [execLoading, setExecLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const { subscription, loading: subscriptionLoading, error: subscriptionError, reload: reloadSubscription } = useCurrentSubscription();
  const metrics = useMetricDictionary();
  const identity = useContext(IdentityContext);
  const [params, setParams] = useSearchParams();
  const query = useMemo(() => parseExecutiveQuery(params), [params]);
  const awaitingCustomRange = query.period === 'custom' && !(query.from && query.to);

  // EPN-79 — the server decides the state. An expired trial gets the limited dashboard (Trial System SRS §19):
  // the payroll summary is never even requested for it (Access Restriction SRS §29 — restriction is applied
  // BEFORE protected data is fetched, not by hiding it afterwards).
  const trialExpired = subscription?.effectiveState === 'TrialExpired';

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    setSummaryError(null);
    try {
      setSummary(await dashboardGateway.getSummary());
    } catch (err) {
      setSummaryError(friendlyErrorMessage(err, 'Could not load the dashboard.'));
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  const loadExecutive = useCallback(async (signal: AbortSignal) => {
    setExecLoading(true);
    setExecError(null);
    try {
      const result = await dashboardGateway.getExecutiveDashboard(query, signal);
      if (!signal.aborted) setExec(result);
    } catch (err) {
      if (!signal.aborted) setExecError(friendlyErrorMessage(err, 'Could not load the payroll cost figures.'));
    } finally {
      if (!signal.aborted) setExecLoading(false);
    }
  }, [query]);

  // The monthly summary (taxes due, unpaid amounts, announcements) does not depend on the chosen period.
  useEffect(() => {
    // Wait for the subscription state to resolve first — never briefly render (or fetch) the full dashboard
    // for a company whose trial has in fact expired.
    if (subscriptionLoading) return;
    if (trialExpired) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSummary(null);
      setSummaryError(null);
      setSummaryLoading(false);
      return;
    }
    void loadSummary();
  }, [loadSummary, subscriptionLoading, trialExpired]);

  useEffect(() => {
    if (subscriptionLoading) return undefined;
    if (trialExpired) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setExec(null);
      setExecError(null);
      setExecLoading(false);
      return undefined;
    }
    if (awaitingCustomRange) {
      setExecLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    void loadExecutive(controller.signal);
    return () => controller.abort();
    // reloadKey is a deliberate trigger: Retry and a saved time zone re-ask the server for the same period.
  }, [loadExecutive, subscriptionLoading, trialExpired, awaitingCustomRange, reloadKey]);

  const activeMembership = identity?.me?.memberships.find((m) => m.companyId === identity.me?.activeCompanyId);
  const loading = subscriptionLoading || (summaryLoading && execLoading && !summary && !exec);
  const period = exec?.period ?? summary?.period ?? null;
  const generatedAtUtc = exec?.generatedAtUtc ?? summary?.generatedAtUtc ?? null;

  const changeQuery = (next: ExecutiveQuery) => setParams(toParams(next));
  const reloadAll = () => {
    void loadSummary();
    setReloadKey((k) => k + 1);
  };

  return (
    <div className="admin-dashboard-embed">
      {/* Trial/subscription state sits ABOVE the screenshotted content root: it is layered in, not a new dashboard. */}
      <TrialStatusCard subscription={subscription} loading={subscriptionLoading} error={subscriptionError} onRetry={reloadSubscription} />

      <div className="escochex-dashboard admin-dashboard-embed__scope" data-testid="admin-dashboard-content-root">
        {loading && <AdminDashboardSkeleton />}

        {!subscriptionLoading && trialExpired && (
          <div className="ed-card ed-empty-state" data-testid="admin-dashboard-limited-state">
            Your dashboard is limited because your trial has ended. Payroll summaries are available again once a
            subscription is active — your setup data remains saved.
          </div>
        )}

        {!subscriptionLoading && !trialExpired && !loading && (
          <>
            {useMocks && (
              <p className="ed-synthetic-banner" role="note" data-testid="synthetic-demo-banner">
                <StatusBadge kind="info">{SYNTHETIC_DEMO_LABEL}</StatusBadge> The figures below are generated for demonstration and are not any company&apos;s payroll.
              </p>
            )}

            {period && generatedAtUtc && (
              <ExecutiveDashboardHeader
                companyName={activeMembership?.companyName ?? null}
                roleName={activeMembership?.roleName ?? null}
                planName={subscription?.planName ?? null}
                period={period}
                generatedAtUtc={generatedAtUtc}
                savedTimeZoneId={identity?.me?.activeCompanyTimeZoneId ?? null}
                onTimeZoneSaved={reloadAll}
              />
            )}

            <PeriodControls key={`${query.period}|${query.from ?? ''}|${query.to ?? ''}`} value={query} onChange={changeQuery} />

            {(summaryError || execError) && (
              <div className="ed-card" style={{ marginBottom: 16 }} role="alert">
                <p className="ed-section-error" style={{ padding: 0 }}>{execError ?? summaryError}</p>
                <button type="button" onClick={reloadAll}>Retry</button>
              </div>
            )}

            {awaitingCustomRange && (
              <div className="ed-card ed-empty-state" data-testid="awaiting-custom-range">
                Choose the first and last month above, then press Apply.
              </div>
            )}

            {!awaitingCustomRange && !execError && (
              execLoading && !exec
                ? <AdminDashboardSkeleton />
                : exec && <AdminDashboardContent exec={exec} summary={summary} metrics={metrics} />
            )}

            {!awaitingCustomRange && execError && summary && <SummaryOnly summary={summary} />}

            <NotShownYet metrics={metrics} extra={exec?.unavailable ?? []} />

            <div className="ed-section-row ed-section-row--single">
              <div className="ed-section-col">
                <AdminProductOfferingsRow />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function AdminDashboardSkeleton() {
  return (
    <>
      <div className="ed-skeleton" style={{ height: 96, borderRadius: 14, marginBottom: 16 }} />
      <div className="ed-summary-row">
        <div className="ed-stat-grid">
          {[0, 1, 2, 3].map((i) => <div key={i} className="ed-skeleton" style={{ height: 148, borderRadius: 14 }} />)}
        </div>
        <div className="ed-skeleton" style={{ flex: 1, borderRadius: 14 }} />
        <div className="ed-skeleton" style={{ flex: 1, borderRadius: 14 }} />
      </div>
      <div className="ed-skeleton" style={{ height: 220, borderRadius: 14, marginBottom: 16 }} />
    </>
  );
}

const PAYROLL_UNAVAILABLE_REASON = 'The payroll service could not be reached, so this figure cannot be shown. Other sections still work.';

const signed = (amount: number) => `${amount >= 0 ? '+' : '-'}${formatCurrency(Math.abs(amount))}`;

/** The variance as a badge (arrow, percentage, and a word for screen readers) and a sentence with the dollar amount. */
function varianceProps(variance: Variance | null, comparisonLabel: string) {
  if (variance === null) return { trend: null, comparisonText: `vs ${comparisonLabel.toLowerCase()}` };
  return {
    trend: variance.percent === null ? null : { isUp: variance.isIncrease, percentChange: Math.abs(variance.percent) },
    comparisonText: `${signed(variance.amount)} vs ${comparisonLabel.toLowerCase()}`,
  };
}

function AdminDashboardContent({
  exec, summary, metrics,
}: {
  exec: ExecutiveDashboardResponse;
  summary: DashboardSummaryResponse | null;
  metrics: MetricDictionaryView;
}) {
  const current = exec.current;
  const comparison = exec.comparison;
  const payrollUnavailable = exec.availability.payrollUnavailable;
  const info = (id: string) => <MetricInfo metric={metrics.get(id)} />;
  const cost = varianceProps(exec.totalCostVariance, exec.period.comparisonLabel);
  const noPayrollAtAll = exec.trend !== null && exec.trend.every((p) => p.totalEmployerCost === 0);

  return (
    <>
      {noPayrollAtAll && !payrollUnavailable && (
        <div className="ed-card ed-empty-state" data-testid="admin-dashboard-empty-state">
          No payroll has been processed for this company in the last 12 months. Once a payroll run is approved, this dashboard
          will show your payroll cost, taxes, and breakdowns.
        </div>
      )}

      <div className="ed-stat-grid ed-stat-grid--four">
        <MoneyStatCardBody
          label="Total Employer Payroll Cost" amount={current?.totalEmployerCost ?? null} trend={cost.trend} trendNeutral
          icon={<i className="fa fa-money" aria-hidden="true" />} destinationHref="/reports" comparisonText={cost.comparisonText}
          unavailableReason={PAYROLL_UNAVAILABLE_REASON} extraHeaderContent={info('total-employer-payroll-cost')}
        />
        <CountStatCardBody
          label="Employees Paid" count={current?.employeesPaid ?? null} trend={null}
          icon={<i className="fa fa-users" aria-hidden="true" />} destinationHref="/people"
          comparisonText={comparison ? `${comparison.employeesPaid} in ${exec.period.comparisonLabel.toLowerCase()}` : `vs ${exec.period.comparisonLabel.toLowerCase()}`}
          unavailableReason={PAYROLL_UNAVAILABLE_REASON} extraHeaderContent={info('total-employees-paid')}
        />
        <MoneyStatCardBody
          label="Average Cost per Paid Employee" amount={current?.averageEmployerCostPerPaidEmployee ?? null} trend={null}
          icon="Ø"
          comparisonText={comparison?.averageEmployerCostPerPaidEmployee != null
            ? `${formatCurrency(comparison.averageEmployerCostPerPaidEmployee)} in ${exec.period.comparisonLabel.toLowerCase()}`
            : `vs ${exec.period.comparisonLabel.toLowerCase()}`}
          unavailableTitle={payrollUnavailable ? undefined : 'No employees paid'}
          unavailableReason={payrollUnavailable ? PAYROLL_UNAVAILABLE_REASON : 'Nobody was paid in this period, so there is no average to show.'}
          extraHeaderContent={info('average-employer-cost-per-paid-employee')}
        />
        <MoneyStatCardBody
          label="Taxes Due This Month" amount={summary ? summary.totalTaxesDue.amount : null} trend={summary?.totalTaxesDue.trend ?? null}
          icon={<i className="fa fa-file-text-o" aria-hidden="true" />} destinationHref="/payroll-taxes" comparisonText="vs last month"
          unavailableReason="The tax service could not be reached, so this figure cannot be shown." extraHeaderContent={info('total-taxes-due')}
        />
      </div>

      <div className="ed-chart-pair">
        <div className="ed-card ed-chart-card" data-testid="admin-dashboard-cost-composition-card">
          <div className="ed-chart-header">
            <span className="ed-chart-period">{exec.period.label}</span>
            <span className="ed-icon-circle" aria-hidden="true"><i className="fa fa-pie-chart" aria-hidden="true" /></span>
          </div>
          <h3 className="ed-chart-title">Where Payroll Cost Went{info('total-employer-payroll-cost')}</h3>
          {current === null ? (
            <UnavailableState reason={PAYROLL_UNAVAILABLE_REASON} />
          ) : current.totalEmployerCost === 0 ? (
            <>
              <p className="ed-chart-amount">{formatCurrency(0)}</p>
              <p className="ed-donut-empty">No payroll approved in this period.</p>
            </>
          ) : (
            <CostComposition current={current} metrics={metrics} />
          )}
        </div>

        <LaborCostTrend trend={exec.trend} period={exec.period} metrics={metrics} />
      </div>

      <div className="ed-chart-pair">
        <div className="ed-card ed-chart-card ed-chart-card--unavailable" data-testid="admin-dashboard-tax-breakdown-card">
          <div className="ed-chart-header">
            <span className="ed-chart-period">{exec.period.label}</span>
            <span className="ed-icon-circle" aria-hidden="true"><i className="fa fa-bar-chart" aria-hidden="true" /></span>
          </div>
          <h3 className="ed-chart-title">Payroll Tax Breakdown</h3>
          {/* The approved second donut (California State Tax / Social Security / Medicare / Federal Income Tax / Local Taxes/SDI). None of these
              categories has a truthful EPN data source today (the tax engine's itemized lines are mock-rate or combined, with no state or local
              line), so this card shows an honest unavailable state, never data. */}
          <div className="ed-donut-row">
            <ul className="ed-donut-legend">
              {['California State Tax', 'Social Security', 'Medicare', 'Federal Income Tax', 'Local Taxes / SDI'].map((label) => (
                <li key={label} className="ed-donut-legend-item ed-donut-legend-item--unavailable">
                  <span className="ed-donut-legend-swatch ed-donut-legend-swatch--unavailable" aria-hidden="true" />
                  {label}
                </li>
              ))}
            </ul>
            <div className="ed-donut-placeholder" role="img" aria-label="Payroll tax category chart: data unavailable">
              <span className="ed-donut-placeholder-text">Data unavailable</span>
            </div>
          </div>
          <p className="ed-donut-empty ed-donut-empty--footnote">
            Jurisdiction-level tax detail is not yet supplied by the payroll tax engine for these categories.
          </p>
        </div>

        <PayrollQueuePanel statuses={exec.payrollStatuses} />
      </div>

      <div className="ed-section-row">
        <div className="ed-section-col">
          <ExecutiveAlertsPanel alerts={exec.alerts} unavailable={exec.availability.taxesUnavailable || exec.availability.runsUnavailable} />
        </div>
        <div className="ed-section-col">
          <ObligationsPanel
            obligations={exec.obligations}
            unpaid={summary?.alerts ?? []}
            obligationsUnavailable={exec.availability.obligationsUnavailable}
            taxesUnavailable={exec.availability.taxesUnavailable || summary === null || summary.availability.taxesUnavailable}
          />
        </div>
      </div>

      <div className="ed-section-row ed-section-row--single">
        <div className="ed-section-col">
          <AnnouncementsPanel announcements={summary?.announcements ?? []} showBody />
        </div>
      </div>
    </>
  );
}

/** When the executive call fails but the monthly summary loaded, still show what the summary knows. */
function SummaryOnly({ summary }: { summary: DashboardSummaryResponse }) {
  return (
    <div className="ed-section-row ed-section-row--single">
      <div className="ed-section-col">
        <AnnouncementsPanel announcements={summary.announcements} showBody />
      </div>
    </div>
  );
}

/** The donut, its exact figures, the proof it adds up, and the two cost components that are not yet part of the total. */
function CostComposition({ current, metrics }: { current: ExecutiveCostTotals; metrics: MetricDictionaryView }) {
  const segments = [
    { label: 'Net pay', amount: current.netPay, color: PAYROLL_BREAKDOWN_COLORS.netPay },
    { label: 'Employee taxes withheld', amount: current.employeeTaxes, color: PAYROLL_BREAKDOWN_COLORS.employeeTaxes },
    { label: 'Employee deductions', amount: current.employeeDeductions, color: PAYROLL_BREAKDOWN_COLORS.employeeDeductions },
    { label: 'Employer payroll taxes', amount: current.employerTaxes, color: PAYROLL_BREAKDOWN_COLORS.employerTaxes },
  ];
  const reconciled = current.reconciliationDifference === 0;
  const benefits = metrics.get('employer-paid-benefits');

  return (
    <>
      <p className="ed-chart-amount">{formatCurrency(current.totalEmployerCost)}</p>
      <div className="ed-donut-row">
        <ul className="ed-donut-legend">
          {segments.map((s) => (
            <li key={s.label} className="ed-donut-legend-item">
              <span className="ed-donut-legend-swatch" style={{ background: s.color }} aria-hidden="true" />
              {s.label}: {formatCurrency(s.amount)}
            </li>
          ))}
        </ul>
        <DonutChart segments={segments} total={current.totalEmployerCost} />
      </div>
      <p className="ed-cost-note">
        Gross wages {formatCurrency(current.grossWages)} = net pay + taxes withheld + deductions. Total adds the employer&apos;s own payroll taxes.
        {' '}
        {reconciled
          ? <StatusBadge kind="good">Reconciled</StatusBadge>
          : <StatusBadge kind="critical">{`Does not reconcile: ${formatCurrency(Math.abs(current.reconciliationDifference))} difference`}</StatusBadge>}
      </p>
      <p className="ed-cost-note ed-cost-note--muted">
        Not included yet: employer-paid benefits and payroll service fees{benefits?.unavailableReason ? '. See "What this page cannot show yet" below.' : '.'}
      </p>
      <ChartDataTable
        caption="Payroll cost by component"
        rows={segments.map((s) => ({ label: s.label, value: s.amount }))}
        total={{ label: 'Total employer payroll cost', value: current.totalEmployerCost }}
        formatValue={formatCurrency}
      />
    </>
  );
}

/** Everything the page cannot show, each with its reason: the metrics the dictionary marks unavailable, and the cuts the executive view cannot supply yet. */
function NotShownYet({ metrics, extra }: { metrics: MetricDictionaryView; extra: { id: string; label: string; reason: string }[] }) {
  if (metrics.unavailable.length === 0 && extra.length === 0) return null;
  return (
    <section className="ed-card ed-gaps-card" data-testid="admin-dashboard-not-shown-yet" aria-labelledby="ed-gaps-title">
      <h3 id="ed-gaps-title" className="ed-chart-title">What this page cannot show yet</h3>
      <p className="ed-donut-empty ed-donut-empty--footnote">
        These figures have no authoritative source in EPN today, so they are left out rather than estimated.
      </p>
      <ul className="ed-gaps">
        {extra.map((item) => (
          <li key={item.id}>
            <strong>{item.label}.</strong> {item.reason}
          </li>
        ))}
        {metrics.unavailable.map((m) => (
          <li key={m.metricId}>
            <strong>{m.displayName}.</strong> {m.unavailableReason}
          </li>
        ))}
      </ul>
    </section>
  );
}
